import {
  Box, Button, Flex, Icon, IconButton, Input, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Table, Tbody, Td,
  Text, Th, Thead, Tr, useColorModeValue, useDisclosure, useToast, FormControl,
  FormLabel, VStack, Badge, Spinner, HStack, Divider, Grid, GridItem,
  InputGroup, InputRightElement,
} from '@chakra-ui/react';
import {
  createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import * as React from 'react';
import {
  MdSearch, MdVisibility, MdDelete, MdPayment, MdUpdate, MdCalendarToday, MdPerson,
} from 'react-icons/md';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import {
  fetchPoojaBookings, updateOrderStatus, updatePaymentStatus, deletePoojaBooking,
} from '../../../../Service/PoojaBookingService';

const columnHelper = createColumnHelper();

const ORDER_STATUSES = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'];
const PAYMENT_STATUSES = ['PENDING', 'PAID', 'FAILED', 'REFUNDED'];

const orderStatusColor = { PENDING: 'yellow', CONFIRMED: 'blue', COMPLETED: 'green', CANCELLED: 'red' };
const paymentStatusColor = { PENDING: 'yellow', PAID: 'green', FAILED: 'red', REFUNDED: 'purple' };

export default function PoojaBookingTable() {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filterOrder, setFilterOrder] = React.useState('');
  const [filterPayment, setFilterPayment] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const pageSize = 10;

  const [selectedBooking, setSelectedBooking] = React.useState(null);
  const [updatingOrderStatus, setUpdatingOrderStatus] = React.useState('');
  const [updatingPaymentStatus, setUpdatingPaymentStatus] = React.useState('');
  const [statusLoading, setStatusLoading] = React.useState(false);

  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [deletingId, setDeletingId] = React.useState(null);

  const toast = useToast();

  // All useColorModeValue hooks at the top level
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const orangeBg = useColorModeValue('orange.50', 'whiteAlpha.100');
  const blueBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const purpleBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'whiteAlpha.200');
  const yellowBg = useColorModeValue('yellow.50', 'whiteAlpha.100');

  const loadBookings = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchPoojaBookings({
      search,
      orderStatus: filterOrder,
      paymentStatus: filterPayment,
    });
    if (result.success) setData(result.data);
    setLoading(false);
  }, [search, filterOrder, filterPayment]);

  React.useEffect(() => { loadBookings(); }, [loadBookings]);

  const paginatedData = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage]);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));

  const handleViewDetail = (row) => {
    setSelectedBooking(row);
    setUpdatingOrderStatus(row.orderStatus || 'PENDING');
    setUpdatingPaymentStatus(row.paymentStatus || 'PENDING');
    onDetailOpen();
  };

  const handleUpdateOrderStatus = async () => {
    if (!selectedBooking) return;
    setStatusLoading(true);
    const result = await updateOrderStatus(selectedBooking.id, updatingOrderStatus);
    setStatusLoading(false);
    if (result.success) {
      toast({ title: 'Order status updated!', status: 'success', duration: 2000 });
      setSelectedBooking(prev => ({ ...prev, orderStatus: updatingOrderStatus }));
      loadBookings();
    } else {
      toast({ title: 'Error', description: result.error, status: 'error', duration: 3000 });
    }
  };

  const handleUpdatePaymentStatus = async () => {
    if (!selectedBooking) return;
    setStatusLoading(true);
    const result = await updatePaymentStatus(selectedBooking.id, updatingPaymentStatus);
    setStatusLoading(false);
    if (result.success) {
      toast({ title: 'Payment status updated!', status: 'success', duration: 2000 });
      setSelectedBooking(prev => ({ ...prev, paymentStatus: updatingPaymentStatus }));
      loadBookings();
    } else {
      toast({ title: 'Error', description: result.error, status: 'error', duration: 3000 });
    }
  };

  const handleDeleteConfirm = (id) => {
    setDeletingId(id);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    const result = await deletePoojaBooking(deletingId);
    if (result.success) {
      toast({ title: 'Booking deleted', status: 'success', duration: 2000 });
      loadBookings();
    } else {
      toast({ title: 'Error', description: result.error, status: 'error', duration: 3000 });
    }
    onDeleteClose();
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const columns = [
    // columnHelper.accessor('orderId', {
    //   header: 'Order ID',
    //   cell: info => <Text fontWeight="700" fontSize="sm" color="brand.500">{info.getValue()}</Text>,
    // }),
    columnHelper.accessor('poojaName', {
      header: 'Pooja',
      cell: info => <Text fontWeight="600" fontSize="sm" color={textColor}>{info.getValue()}</Text>,
    }),
    columnHelper.display({
      id: 'devotee',
      header: 'Devotee',
      cell: info => {
        const d = info.row.original.devoteeDetails || {};
        return (
          <VStack align="start" spacing={0}>
            <Text fontSize="sm" fontWeight="600" color={textColor}>{d.name || '—'}</Text>
            <Text fontSize="xs" color="gray.500">{d.phone || ''}</Text>
          </VStack>
        );
      },
    }),
    columnHelper.accessor('bookingType', {
      header: 'Type',
      cell: info => (
        <Badge colorScheme={info.getValue() === 'FAMILY' ? 'purple' : 'blue'} fontSize="xs">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('totalAmount', {
      header: 'Amount',
      cell: info => <Text fontSize="sm" fontWeight="700" color="green.500">₹{info.getValue()}</Text>,
    }),
    columnHelper.accessor('orderStatus', {
      header: 'Order',
      cell: info => (
        <Badge colorScheme={orderStatusColor[info.getValue()] || 'gray'} fontSize="xs">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('paymentStatus', {
      header: 'Payment',
      cell: info => (
        <Badge colorScheme={paymentStatusColor[info.getValue()] || 'gray'} fontSize="xs">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Date',
      cell: info => <Text fontSize="xs" color="gray.500">{formatDate(info.getValue())}</Text>,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: info => (
        <HStack spacing={1}>
          <IconButton
            icon={<Icon as={MdVisibility} />}
            size="sm"
            colorScheme="brand"
            variant="ghost"
            aria-label="View"
            onClick={() => handleViewDetail(info.row.original)}
          />
          <IconButton
            icon={<Icon as={MdDelete} />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            aria-label="Delete"
            onClick={() => handleDeleteConfirm(info.row.original.id)}
          />
        </HStack>
      ),
    }),
  ];

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX="auto">
      {/* Header */}
      <Flex px="25px" py="15px" justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Text color={textColor} fontSize="22px" fontWeight="700">Pooja Booking Management</Text>
        <HStack spacing={3} flexWrap="wrap">
          <InputGroup w="200px">
            <Input
              placeholder="Search bookings..."
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              size="sm"
              borderRadius="8px"
            />
            <InputRightElement pointerEvents="none" h="32px">
              <Icon as={MdSearch} color="gray.400" />
            </InputRightElement>
          </InputGroup>
          <Select
            size="sm"
            borderRadius="8px"
            w="150px"
            value={filterOrder}
            placeholder="Order Status"
            onChange={e => { setFilterOrder(e.target.value); setFilterPayment(''); setCurrentPage(1); }}
          >
            {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Select
            size="sm"
            borderRadius="8px"
            w="160px"
            value={filterPayment}
            placeholder="Payment Status"
            onChange={e => { setFilterPayment(e.target.value); setFilterOrder(''); setCurrentPage(1); }}
          >
            {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
      </Flex>

      {/* Table */}
      {loading ? (
        <Flex justify="center" align="center" py="60px"><Spinner size="lg" color="brand.500" /></Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" color="gray.500">
            <Thead>
              {table.getHeaderGroups().map(hg => (
                <Tr key={hg.id}>
                  {hg.headers.map(h => (
                    <Th key={h.id} borderColor={borderColor} pe="10px" ps="25px" py="12px">
                      <Flex align="center" fontSize="xs" fontWeight="600" color="gray.400">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center" py="40px">
                    <Text color="gray.400">No bookings found</Text>
                  </Td>
                </Tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <Tr key={row.id} _hover={{ bg: hoverBg }}>
                    {row.getVisibleCells().map(cell => (
                      <Td key={cell.id} borderColor={borderColor} ps="25px" pe="10px" py="14px">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      <Flex px="25px" py="15px" justify="space-between" align="center">
        <Text fontSize="sm" color="gray.500">
          {data.length} bookings total
        </Text>
        <HStack>
          <IconButton
            icon={<Icon as={IoChevronBack} />}
            size="sm"
            variant="outline"
            isDisabled={currentPage === 1}
            onClick={() => setCurrentPage(p => p - 1)}
            aria-label="Previous"
          />
          <Text fontSize="sm" fontWeight="600">{currentPage} / {totalPages}</Text>
          <IconButton
            icon={<Icon as={IoChevronForward} />}
            size="sm"
            variant="outline"
            isDisabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => p + 1)}
            aria-label="Next"
          />
        </HStack>
      </Flex>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Booking Detail — <Text as="span" color="brand.500">{selectedBooking.orderId}</Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={5} align="stretch">

                {/* Pooja Info */}
                <Box p={4} bg={orangeBg} borderRadius="10px">
                  <Text fontWeight="700" fontSize="md" mb={2} color="orange.600">🕉 Pooja Info</Text>
                  <Grid templateColumns="1fr 1fr" gap={3}>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">Pooja Name</Text>
                      <Text fontWeight="600" fontSize="sm">{selectedBooking.poojaName}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">Booking Type</Text>
                      <Badge colorScheme={selectedBooking.bookingType === 'FAMILY' ? 'purple' : 'blue'}>
                        {selectedBooking.bookingType}
                      </Badge>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">Booking Date</Text>
                      <Text fontWeight="600" fontSize="sm">{formatDate(selectedBooking.bookingDate)}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">Total Amount</Text>
                      <Text fontWeight="700" fontSize="sm" color="green.500">₹{selectedBooking.totalAmount}</Text>
                    </GridItem>
                  </Grid>
                </Box>

                {/* Devotee Details */}
                <Box p={4} bg={blueBg} borderRadius="10px">
                  <Text fontWeight="700" fontSize="md" mb={2} color="blue.600">👤 Devotee Details</Text>
                  <Grid templateColumns="1fr 1fr" gap={3}>
                    {Object.entries(selectedBooking.devoteeDetails || {}).map(([k, v]) => (
                      <GridItem key={k}>
                        <Text fontSize="xs" color="gray.500" textTransform="capitalize">{k}</Text>
                        <Text fontWeight="600" fontSize="sm">{v || '—'}</Text>
                      </GridItem>
                    ))}
                  </Grid>
                </Box>

                {/* Family Members */}
                {(selectedBooking.familyMembers || []).length > 0 && (
                  <Box p={4} bg={purpleBg} borderRadius="10px">
                    <Text fontWeight="700" fontSize="md" mb={2} color="purple.600">👨‍👩‍👧 Family Members</Text>
                    <VStack spacing={2} align="stretch">
                      {selectedBooking.familyMembers.map((m, i) => (
                        <Box key={i} p={2} bg={cardBg} borderRadius="8px">
                          <HStack justify="space-between">
                            <Text fontWeight="600" fontSize="sm">{m.name}</Text>
                            <Badge colorScheme="purple" fontSize="xs">{m.relation}</Badge>
                          </HStack>
                          <Text fontSize="xs" color="gray.500">Gothram: {m.gothram || '—'}</Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {selectedBooking.specialNotes && (
                  <Box p={3} borderLeft="4px solid" borderColor="yellow.400" bg={yellowBg} borderRadius="0 8px 8px 0">
                    <Text fontSize="xs" color="gray.500">Special Notes</Text>
                    <Text fontSize="sm">{selectedBooking.specialNotes}</Text>
                  </Box>
                )}

                <Divider />

                {/* Status Update */}
                <Box>
                  <Text fontWeight="700" fontSize="md" mb={3} color={textColor}>Update Status</Text>
                  <Grid templateColumns="1fr 1fr" gap={4}>
                    <Box>
                      <FormLabel fontSize="sm" mb={1}>Order Status</FormLabel>
                      <HStack>
                        <Select
                          size="sm"
                          value={updatingOrderStatus}
                          onChange={e => setUpdatingOrderStatus(e.target.value)}
                        >
                          {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </Select>
                        <Button
                          size="sm"
                          colorScheme="brand"
                          onClick={handleUpdateOrderStatus}
                          isLoading={statusLoading}
                          leftIcon={<Icon as={MdUpdate} />}
                        >
                          Update
                        </Button>
                      </HStack>
                    </Box>
                    <Box>
                      <FormLabel fontSize="sm" mb={1}>Payment Status</FormLabel>
                      <HStack>
                        <Select
                          size="sm"
                          value={updatingPaymentStatus}
                          onChange={e => setUpdatingPaymentStatus(e.target.value)}
                        >
                          {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </Select>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={handleUpdatePaymentStatus}
                          isLoading={statusLoading}
                          leftIcon={<Icon as={MdPayment} />}
                        >
                          Update
                        </Button>
                      </HStack>
                    </Box>
                  </Grid>
                </Box>

              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onDetailClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Booking</ModalHeader>
          <ModalCloseButton />
          <ModalBody><Text>Are you sure you want to delete this booking?</Text></ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}